import type { SearchGatedUserProfile } from '../types/search-gated-user-profile.type';

/**
 * The only fields the name fallback needs. Widened from
 * `Partial<SearchGatedUserProfile>` so a UserProfile-shaped recipient (numeric id) can be
 * named too — Talent Search messaging holds those, not search hits.
 */
export type CandidateNameFields = Partial<
  Pick<SearchGatedUserProfile, 'nameFirst' | 'nameLast'>
> & {
  id?: string | number;
};

/** The name parts to substitute into message text sent to a candidate. */
export interface OutboundCandidateName {
  /** `nameFirst nameLast`, either part alone, or `''` when the candidate has neither. */
  fullName: string;
  /** `nameFirst`, or `''` when it is missing. */
  firstName: string;
}

/**
 * Builds the name for text a candidate will actually read. Unlike
 * `getNameForCandidate()` — a UI label helper — this never invents a placeholder: its
 * `Unknown (<id>)` fallback would send FactoryFix's internal profile id to the candidate
 * as their own name. A candidate with no name on record yields empty strings, which
 * `TemplateService.mapTemplatePlaceholders()` honors verbatim.
 */
export function getOutboundNameForCandidate(
  candidate?: CandidateNameFields,
): OutboundCandidateName {
  const firstName = candidate?.nameFirst?.trim() ?? '';
  const lastName = candidate?.nameLast?.trim() ?? '';

  return {
    fullName: [firstName, lastName].filter(Boolean).join(' '),
    firstName,
  };
}

export function getNameForCandidate(candidate?: CandidateNameFields): string {
  const unknownString = 'Unknown';
  if (candidate) {
    if (!candidate.nameFirst) {
      // Don't have first name
      if (!candidate.nameLast) {
        if (!candidate.id) {
          return unknownString;
        } else {
          return `${unknownString} (${candidate.id})`;
        }
      } else {
        return candidate.nameLast;
      }
    } else {
      // Have a first name
      // eslint-disable-next-line no-lonely-if
      if (!candidate.nameLast) {
        return candidate.nameFirst;
      } else {
        // Has everything, add a space!
        return `${candidate.nameFirst} ${candidate.nameLast}`;
      }
    }
  } else {
    return unknownString;
  }
}
